import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { cn } from '../lib/utils';

export function SEO({ title, description }) {
  const fullTitle = `${title} | Sorgskab`;
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
    </Helmet>
  );
}

export function Button({ children, to, variant = 'primary', className, ...props }) {
  const variants = {
    primary: 'bg-sorg-earth text-white hover:bg-sorg-dark',
    secondary: 'bg-white text-sorg-earth border border-sorg-earth/20 hover:bg-sorg-beige/20',
    outline: 'bg-transparent text-sorg-earth border border-sorg-earth hover:bg-sorg-earth hover:text-white',
  };

  const baseStyles = 'inline-flex items-center justify-center px-6 py-2.5 md:px-8 md:py-3 rounded-full text-sm font-medium transition-all duration-300 soft-shadow tracking-wide';

  if (to) {
    const isAnchorOnSamePage = typeof to === 'string' && to.startsWith('#');

    const handleClick = (e) => {
      if (props.onClick) {
        props.onClick(e);
      }
      if (isAnchorOnSamePage) {
        const id = to.replace('#', '');
        const target = document.getElementById(id);
        if (target) {
          e.preventDefault();
          target.scrollIntoView({ behavior: 'smooth' });
          window.history.pushState(null, '', to);
        }
      }
    };

    return (
      <Link to={to} onClick={handleClick} className={cn(baseStyles, variants[variant], className)} {...props}>
        {children}
      </Link>
    );
  }

  return (
    <button className={cn(baseStyles, variants[variant], className)} {...props}>
      {children}
    </button>
  );
}

export function Section({ children, className, containerClassName, id, glass = false }) {
  return (
    <section 
      id={id} 
      className={cn(
        'py-12 md:py-16 lg:py-24 px-6 overflow-hidden transition-all duration-500', 
        glass && 'bg-white/30 backdrop-blur-md border-y border-white/20',
        className
      )}
    >
      <div className={cn('max-w-7xl mx-auto', containerClassName)}>
        {children}
      </div>
    </section>
  );
}

export function FadeIn({ children, delay = 0, direction = 'up', className }) {
  const directions = {
    up: { y: 20 },
    down: { y: -20 },
    left: { x: 20 },
    right: { x: -20 },
    none: { x: 0, y: 0 },
  };

  return (
    <motion.div
      initial={{ opacity: 0, ...directions[direction] }}
      whileInView={{ opacity: 1, x: 0, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, delay, ease: [0.21, 0.47, 0.32, 0.98] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function LotusIcon({ className }) {
  return (
    <svg 
      viewBox="0 0 100 100" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg" 
      className={cn("w-12 h-12", className)}
    >
      {/* Central petal - extremely delicate */}
      <path 
        d="M50 92C50 92 46 68 46 48C46 28 50 12 50 12C50 12 54 28 54 48C54 68 50 92 50 92Z" 
        stroke="currentColor" 
        strokeWidth="0.1" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      />
      {/* Inner side petals - flowing lines */}
      <path 
        d="M48 90C48 90 30 80 30 54C30 28 48 24 48 24" 
        stroke="currentColor" 
        strokeWidth="0.08" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        opacity="0.7"
      />
      <path 
        d="M52 90C52 90 70 80 70 54C70 28 52 24 52 24" 
        stroke="currentColor" 
        strokeWidth="0.08" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        opacity="0.7"
      />
      {/* Middle side petals */}
      <path 
        d="M46 88C46 88 15 78 15 50C15 22 42 20 42 20" 
        stroke="currentColor" 
        strokeWidth="0.06" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        opacity="0.5"
      />
      <path 
        d="M54 88C54 88 85 78 85 50C85 22 58 20 58 20" 
        stroke="currentColor" 
        strokeWidth="0.06" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        opacity="0.5"
      />
      {/* Outer side petals - very faint */}
      <path 
        d="M44 85C44 85 5 70 5 45C5 20 35 18 35 18" 
        stroke="currentColor" 
        strokeWidth="0.04" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        opacity="0.3"
      />
      <path 
        d="M56 85C56 85 95 70 95 45C95 20 65 18 65 18" 
        stroke="currentColor" 
        strokeWidth="0.04" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        opacity="0.3"
      />
      {/* Bottom delicate curves */}
      <path 
        d="M32 82C32 82 40 90 50 90C60 90 68 82 68 82" 
        stroke="currentColor" 
        strokeWidth="0.05" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        opacity="0.2"
      />
    </svg>
  );
}

export function HeartIcon({ className }) {
  return (
    <svg 
      viewBox="0 0 100 100" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg" 
      className={cn("w-12 h-12", className)}
    >
      <path 
        d="M50 88C50 88 20 70 20 42C20 22 40 18 50 30C60 18 80 22 80 42C80 70 50 88 50 88Z" 
        stroke="currentColor" 
        strokeWidth="0.1" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      />
      <path 
        d="M40 36C40 36 32 30 25 36" 
        stroke="currentColor" 
        strokeWidth="0.05" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        opacity="0.3"
      />
      <path 
        d="M60 36C60 36 68 30 75 36" 
        stroke="currentColor" 
        strokeWidth="0.05" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        opacity="0.3"
      />
    </svg>
  );
}

export function DecorativeElement({ type = 'lotus', className, delay = 0, opacity = 0.3 }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8, rotate: -10 }}
      animate={{ 
        opacity: opacity, 
        scale: 1, 
        rotate: [0, 5, 0],
        y: [0, -20, 0],
        x: [0, 8, 0]
      }}
      transition={{ 
        opacity: { duration: 2, delay },
        scale: { duration: 2, delay },
        y: { duration: 15, repeat: Infinity, ease: "easeInOut" },
        x: { duration: 18, repeat: Infinity, ease: "easeInOut" },
        rotate: { duration: 20, repeat: Infinity, ease: "easeInOut" }
      }}
      className={cn("absolute pointer-events-none text-sorg-earth z-0", className)}
    >
      {type === 'lotus' ? <LotusIcon className="w-full h-full" /> : <HeartIcon className="w-full h-full" />}
    </motion.div>
  );
}

export function DecorativeBackground() {
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none -z-10 bg-stone-50">
      {/* Ultra-soft Mesh Gradient Orbs */}
      
      {/* Soft Warm Orb - Top Left */}
      <motion.div
        animate={{
          x: [0, 100, 0],
          y: [0, 50, 0],
          scale: [1, 1.2, 1],
        }}
        transition={{
          duration: 25,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="absolute -top-[20%] -left-[10%] w-[70%] h-[70%] rounded-full bg-orange-100/40 blur-[160px]"
      />

      {/* Soft Sage Orb - Top Right */}
      <motion.div
        animate={{
          x: [0, -80, 0],
          y: [0, 120, 0],
          scale: [1.1, 0.9, 1.1],
        }}
        transition={{
          duration: 30,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2
        }}
        className="absolute -top-[15%] -right-[10%] w-[60%] h-[60%] rounded-full bg-emerald-50/50 blur-[140px]"
      />

      {/* Soft Earth Orb - Bottom Left */}
      <motion.div
        animate={{
          x: [0, 60, 0],
          y: [0, -100, 0],
          scale: [0.9, 1.1, 0.9],
        }}
        transition={{
          duration: 28,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1
        }}
        className="absolute -bottom-[20%] -left-[15%] w-[80%] h-[80%] rounded-full bg-stone-200/30 blur-[180px]"
      />

      {/* Soft Rose/Beige Orb - Bottom Right */}
      <motion.div
        animate={{
          x: [0, -120, 0],
          y: [0, -60, 0],
          scale: [1, 1.3, 1],
        }}
        transition={{
          duration: 35,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 3
        }}
        className="absolute -bottom-[10%] -right-[15%] w-[70%] h-[70%] rounded-full bg-rose-50/40 blur-[150px]"
      />

      {/* Center Softener */}
      <div className="absolute inset-0 bg-white/10 backdrop-blur-[20px]" />

      {/* Decorative Icons - kept but with lower opacity for better blending */}
      <DecorativeElement type="lotus" className="top-[15%] left-[8%] w-32 h-32 opacity-10" delay={0.5} />
      <DecorativeElement type="heart" className="top-[45%] right-[10%] w-24 h-24 opacity-05" delay={1.2} />
      <DecorativeElement type="lotus" className="bottom-[20%] left-[12%] w-40 h-40 opacity-05" delay={2} />
      <DecorativeElement type="heart" className="bottom-[35%] right-[18%] w-20 h-20 opacity-10" delay={0.8} />
    </div>
  );
}

